import React from 'react';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Skeleton, Box } from '@mui/material';
import { ProductSubscription, Deal, SubscriptionPaymentTerm } from '../../shared/models';
import { EmptyGuid } from '../../shared/constants';
import { CerebroDrawer, CerebroHeading, CerebroSecondaryButton, CerebroText } from '../../shared/components';
import { NumberUtils } from '../../shared/utils';
import { useProductSubscriptionPaymentTermsQuery } from '../../shared/hooks';
import { PaymentTermActionButton } from './PaymentTermActionButton';
import { SubscriptionPaymentTermFormDialog } from './SubscriptionPaymentTermFormDialog';
import { DeleteSubscriptionPaymentTermDialog } from './DeleteSubscriptionPaymentTermDialog';

interface SubscriptionPaymentTermsDrawerProps {
	onClose: () => void;
	subscription: ProductSubscription | null;
	deal: Deal;
}

export const SubscriptionPaymentTermsDrawer: React.FC<SubscriptionPaymentTermsDrawerProps> = ({
	subscription,
	deal,
	onClose,
}) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [termForm, setTermForm] = React.useState<SubscriptionPaymentTerm | null>(null);
	const [termDelete, setTermDelete] = React.useState<SubscriptionPaymentTerm | null>(null);
	const paymentTermsQuery = useProductSubscriptionPaymentTermsQuery(deal.guid, subscription?.guid, {
		enabled: subscription != null,
		retry: false,
	});
	const isFetching = paymentTermsQuery.isLoading || paymentTermsQuery.isFetching;

	const onCloseDialogs = () => {
		setTermForm(null);
		setTermDelete(null);
	};

	const createNewPaymentTerm = () => {
		setTermForm({
			id: 0,
			guid: EmptyGuid,
			amount: 0,
			numberOfDays: 0,
		} as SubscriptionPaymentTerm);
	};

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Update Payment Terms</CerebroHeading>

			<CerebroSecondaryButton onClick={() => createNewPaymentTerm()} sx={{ marginBottom: 2, marginTop: 3 }}>
				Add Payment Term
			</CerebroSecondaryButton>

			<TableContainer sx={{ marginBottom: 8 }}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Amount</TableCell>
							<TableCell>Number of Days</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<TableRow>
								<TableCell colSpan={3}>
									<Skeleton height={60} />
								</TableCell>
							</TableRow>
						)}

						{paymentTermsQuery.isSuccess && (
							<>
								{paymentTermsQuery.data.map(term => {
									return (
										<TableRow key={term.guid}>
											<TableCell>{NumberUtils.FormatCurrency(term.amount)}</TableCell>
											<TableCell>{term.numberOfDays}</TableCell>
											<TableCell>
												<PaymentTermActionButton
													term={term}
													onEdit={setTermForm}
													onDelete={setTermDelete}
												/>
											</TableCell>
										</TableRow>
									);
								})}
								{paymentTermsQuery.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={4}>
											No payment terms are currently associated to the subscription.
										</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Box>
				<CerebroText>Payment Term Validation Rules</CerebroText>
				<ul style={{ lineHeight: 2 }}>
					<li>
						<CerebroText>
							If subscription fee payment schedule is custom, there must be at least one payment term
						</CerebroText>
					</li>
					<li>
						<CerebroText>Total amount of payment terms must equal subscription fee</CerebroText>
					</li>
					<li>
						<CerebroText>
							Number of days sets the number of days after the invoice each payment would be due
						</CerebroText>
					</li>
				</ul>
			</Box>

			<SubscriptionPaymentTermFormDialog
				subscription={subscription}
				term={termForm}
				dealGuid={deal.guid}
				companyGuid={deal.companyGuid}
				close={onCloseDialogs}
			/>
			<DeleteSubscriptionPaymentTermDialog
				subscription={subscription}
				term={termDelete}
				dealGuid={deal.guid}
				companyGuid={deal.companyGuid}
				close={onCloseDialogs}
			/>
		</CerebroDrawer>
	);
};
